import { useApolloClient } from '@apollo/client';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import {
  SuppliersPageChatTaxProfileListDocument,
  useSuppliersPageChatTaxProfileListQuery,
  type ChatTaxProfileListInput,
  type SuppliersPageChatTaxProfileListQuery,
  type SuppliersPageChatTaxProfileListQueryVariables,
} from '../../../../../../../generated';
import {
  type CustomerTaxProfileListResult,
  type CustomerTaxProfileRow,
} from '../types';
import { mapChatTaxProfileResponse } from '../utils/chatTaxProfileMapper';

const PAGE_SIZE = 25;

const LOAD_MORE_ERROR_MESSAGE =
  'Could not load more customers. Press Load more to try again.';

interface UseChatTaxProfileListParams {
  vendorId?: string;
}

interface UseChatTaxProfileListResult {
  customers: CustomerTaxProfileRow[];
  /** First page is loading, so there is still an empty table. */
  isLoading: boolean;
  /** A "Load more" page is loading. */
  isLoadingMore: boolean;
  errorMessage: string | null;
  /** A "Load more" page failed; the rows already loaded stay on screen. */
  loadMoreErrorMessage: string | null;
  hasNextPage: boolean;
  loadMore: () => void;
}

/**
 * Every customer of a distributor that has a tax jurisdiction assigned,
 * accumulating pages for a cursor-based "Load more".
 *
 * `no-cache`: the `ChatTaxProfileListResponse` union members have no `id`, so
 * InMemoryCache cannot normalise them and a cache read drops the
 * inline-fragment `edges`. Reading from the network keeps the response intact.
 */
export const useChatTaxProfileList = ({
  vendorId,
}: UseChatTaxProfileListParams): UseChatTaxProfileListResult => {
  const client = useApolloClient();
  const enabled = Boolean(vendorId);

  const buildInput = useCallback(
    (cursor?: string): ChatTaxProfileListInput => ({
      vendorId: vendorId ?? '',
      // No `chatIds`: the filter narrows the list to named chats, and this
      // table lists every customer that has a profile.
      pagination: { limit: PAGE_SIZE, direction: 'forward', cursor },
    }),
    [vendorId]
  );

  const { data, loading, error } = useSuppliersPageChatTaxProfileListQuery({
    variables: { input: buildInput() },
    skip: !enabled,
    fetchPolicy: 'no-cache',
    notifyOnNetworkStatusChange: true,
    errorPolicy: 'all',
  });

  const mapped = useMemo(
    () => mapChatTaxProfileResponse(data?.chatTaxProfileList),
    [data]
  );

  const [customers, setCustomers] = useState<CustomerTaxProfileRow[]>([]);
  const [pageInfo, setPageInfo] = useState<{
    hasNextPage: boolean;
    endCursor: string;
  } | null>(null);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [loadMoreErrorMessage, setLoadMoreErrorMessage] = useState<
    string | null
  >(null);
  const seededResult = useRef<CustomerTaxProfileListResult | null>(null);
  // Bumped on every reset and read by an in-flight "Load more". A vendor id
  // comparison would let a request survive a move away and back, because the
  // id it started with matches again by the time it lands.
  const generation = useRef(0);

  // Drop the accumulated pages when the distributor changes. Clearing
  // `isLoadingMore` here keeps the button from spinning on forever.
  useEffect(() => {
    generation.current += 1;
    seededResult.current = null;
    setCustomers([]);
    setPageInfo(null);
    setIsLoadingMore(false);
    setLoadMoreErrorMessage(null);
  }, [vendorId]);

  // Seed page one on a fresh network completion. Gating on `!loading` ignores
  // Apollo's synchronous same-variable replay, so the fresh `no-cache`
  // response wins over a stale page 1.
  useEffect(() => {
    if (loading || mapped?.kind !== 'success') {
      return;
    }
    if (seededResult.current === mapped) {
      return;
    }
    seededResult.current = mapped;
    setCustomers(mapped.page.customers);
    setPageInfo(mapped.page.pageInfo);
  }, [loading, mapped]);

  const loadMore = useCallback(() => {
    if (
      !enabled ||
      !pageInfo?.hasNextPage ||
      !pageInfo.endCursor ||
      isLoadingMore
    ) {
      return;
    }

    const cursor = pageInfo.endCursor;
    const requestGeneration = generation.current;
    setIsLoadingMore(true);
    setLoadMoreErrorMessage(null);

    client
      .query<
        SuppliersPageChatTaxProfileListQuery,
        SuppliersPageChatTaxProfileListQueryVariables
      >({
        query: SuppliersPageChatTaxProfileListDocument,
        variables: { input: buildInput(cursor) },
        fetchPolicy: 'no-cache',
        errorPolicy: 'all',
      })
      .then((response) => {
        // Drop a stale page: the distributor changed while it was in flight,
        // and appending it would poison the list and the cursor.
        if (requestGeneration !== generation.current) {
          return;
        }
        const nextPage = mapChatTaxProfileResponse(
          response.data?.chatTaxProfileList
        );
        if (nextPage?.kind === 'success') {
          setCustomers((prev) => [...prev, ...nextPage.page.customers]);
          setPageInfo(nextPage.page.pageInfo);
          return;
        }
        // A failure member on a later page keeps the loaded rows and says why
        // the rest did not arrive.
        setLoadMoreErrorMessage(nextPage?.message ?? LOAD_MORE_ERROR_MESSAGE);
      })
      .catch(() => {
        if (requestGeneration === generation.current) {
          setLoadMoreErrorMessage(LOAD_MORE_ERROR_MESSAGE);
        }
      })
      .finally(() => {
        if (requestGeneration === generation.current) {
          setIsLoadingMore(false);
        }
      });
  }, [enabled, pageInfo, isLoadingMore, client, buildInput]);

  const errorMessage =
    mapped?.kind === 'error' ? mapped.message : (error?.message ?? null);

  return {
    customers,
    isLoading: enabled && loading && customers.length === 0 && !errorMessage,
    isLoadingMore,
    errorMessage,
    loadMoreErrorMessage,
    hasNextPage: Boolean(pageInfo?.hasNextPage),
    loadMore,
  };
};
